import { produce } from "immer";

import makeUpdateModel from "./make_update_model";
import UserPermission from "../../../models/domain/user_permission";
import { USER_PERMISSION_DELETED, UserPermissionDeletedEvent } from "../../../events/superadmin/user_permission_deleted";
import { USER_PERMISSIONS_BY_ID_LOADED, UserPermissionsByIdLoadedEvent } from "../../../events/auth_events/user_permissions_by_id_loaded";
import { USER_PERMISSION_CREATED, UserPermissionCreatedEvent } from "../../../events/superadmin/user_permission_created";
import { ORGANIZATION_MEMBER_REMOVED, OrganizationMemberRemovedEvent } from "../../../events/superadmin/organization_member_removed";

import type { Reducer } from "redux";
import type { ByDatabaseId } from "type_aliases";

export type PermissionsEvents =
  | UserPermissionsByIdLoadedEvent
  | UserPermissionDeletedEvent
  | UserPermissionCreatedEvent
  | OrganizationMemberRemovedEvent

export type PermissionsStore = {
  byId: ByDatabaseId<UserPermission>
}

const updatePermission = makeUpdateModel(new UserPermission({}));

const reducePermissions: Reducer<PermissionsStore, PermissionsEvents> = (permissions = { byId: {} }, event) => {
  if (!permissions.byId) {
    permissions.byId = {};
  }

  switch (event.type) {
    case USER_PERMISSIONS_BY_ID_LOADED: {
      return event.payload.permissions.reduce((permissionsSoFar, permission) => {
        if (permission.id !== null) {
          return updatePermission(permissionsSoFar, permission.id, new UserPermission(permission));
        }

        const syntheticKey = `role-${permission.userId}-${permission.firebaseOrganizationId ?? ""}-${permission.firebaseProjectId ?? ""}-${permission.permissionType}-${permission.permissionAction}`;
        return produce(permissionsSoFar, (draft) => {
          draft.byId[syntheticKey as unknown as number] = new UserPermission(permission);
        });
      }, permissions);
    }
    case USER_PERMISSION_DELETED: {
      return produce(permissions, (draft) => {
        delete draft.byId[event.payload];
      });
    }
    case USER_PERMISSION_CREATED: {
      return updatePermission(permissions, event.payload.id, new UserPermission(event.payload));
    }
    case ORGANIZATION_MEMBER_REMOVED: {
      const { memberId } = event.payload;
      return produce(permissions, (draft) => {
        Object.keys(draft.byId).forEach((key) => {
          if (draft.byId[key as unknown as number].userId === memberId) {
            delete draft.byId[key as unknown as number];
          }
        });
      });
    }
    default: {
      return permissions;
    }
  }
};

export default reducePermissions;

